import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { resolveColor } from '../../theme/colorTokens.js';
import { ALIGN_OPTIONS, marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
/**
 * Shrine `inventory-status` — config-by-config. A stock-level indicator that renders ONE of three
 * states off the product's inventory — "In stock" / "Low stock: {n} left" / "Out of stock" — each with
 * its own dot icon, text colour, optional solid/gradient background, optional border + corner radius.
 *
 * Shrine groups (reproduced exactly): Inventory (when-to-show + threshold + show-quantity), Text &
 * styling (size/font/align), Container (width), Icon (size + animation), In stock / Low stock / Out of
 * stock (the three per-state colour+box blocks), Margins. 29 settings — all implemented below.
 *
 * ID MAPPING (our id ← Shrine id, kept stable): showLowStockWhen←show_low_stock_when,
 * inventoryThreshold←inventory_threshold, showInventoryQuantity←show_inventory_quantity,
 * textSize←text_size, fontFamily←font_family, textAlign←text_align, containerWidth←container_width,
 * iconSize←icon_size, iconAnimation←icon_animation, iconAnimationSpeed←icon_animation_speed,
 * animationScale←animation_scale, blinkShowOuter←blink_show_outer, blinkLowestOpacity←blink_lowest_opacity,
 * {in,low,out}TextColor←{…}_text_color, {…}IconColor←{…}_icon_color, {…}BgType←{…}_bg_type,
 * {…}BgColor←{…}_bg_color, {…}BgGradient←{…}_bg_gradient (our `gradient` field = Shrine `color_background`),
 * {…}BorderThickness←{…}_border_thickness, {…}BorderStyle←{…}_border_style, {…}BorderColor←{…}_border_color,
 * {…}Radius←{…}_radius.
 *
 * INVENTORY SOURCE: the active state is derived from `product.inventory` ({ tracked, quantity }). On a
 * real product with tracking on we read the live `quantity`. When inventory is untracked or `quantity`
 * is null (the built-in demo product, or a non-tracked SKU) we fall back to a sensible demo quantity so
 * the builder preview shows a populated indicator instead of a blank — exactly mirroring how Shrine's
 * theme editor previews a stock chip when no live Shopify inventory is bound.
 *
 * IMPLEMENTED beyond the "Shopify-only" label: the source tags the icon animation knobs
 * (icon_animation/_speed, animation_scale, blink_*) and show_inventory_quantity / inventory_threshold as
 * "shopifyOnly". They are NOT platform-coupled here — the ripple/blink animations are pure CSS keyframes
 * (SSR-safe, degrade to a static dot without JS), the threshold drives our own demo/real comparison, and
 * the quantity comes from `product.inventory.quantity`. So every one of the 29 settings is wired.
 *
 * NOTE on conditional visibility: our `visibleWhen` predicate is equals/notEquals only. Shrine gates the
 * per-state border_style/border_color on `border_thickness > 0` and the radius on
 * `bg_type != none OR border_thickness > 0` (numeric / OR comparisons we can't express in the inspector),
 * so those fields stay always-visible in the inspector — the RENDER still honours thickness 0 (no border)
 * and draws radius only when there is a box, so the output is faithful.
 *
 * DEFERRED (justified): none of the 29 settings. (Live Shopify `inventory_quantity` AJAX refresh on
 * variant change is wired elsewhere with the real cart/variant data layer — the block already re-derives
 * its state from `product.inventory` on each render, so it updates as soon as that state changes.)
 */
const ANIMATION_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'ripple', label: 'Ripple' },
    { value: 'blink', label: 'Blink' },
];
const BG_TYPE_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'color', label: 'Solid color' },
    { value: 'gradient', label: 'Gradient' },
];
const BORDER_STYLE_OPTIONS = [
    { value: 'solid', label: 'Solid' },
    { value: 'dashed', label: 'Dashed' },
];
/** Build the 9 per-state colour+box fields for one state (in / low / out) with Shrine's defaults. */
function stateFields(prefix, group, defaults) {
    return [
        {
            id: `${prefix}TextColor`,
            type: 'color',
            label: 'Text color',
            group,
            default: { ref: 'custom', value: defaults.text },
        },
        {
            id: `${prefix}IconColor`,
            type: 'color',
            label: 'Icon color',
            group,
            default: { ref: 'custom', value: defaults.icon },
        },
        {
            id: `${prefix}BgType`,
            type: 'select',
            label: 'Background',
            group,
            default: 'none',
            options: BG_TYPE_OPTIONS,
        },
        {
            id: `${prefix}BgColor`,
            type: 'color',
            label: 'Background color',
            group,
            default: { ref: 'custom', value: defaults.bgColor },
            visibleWhen: { field: `${prefix}BgType`, equals: 'color' },
        },
        {
            id: `${prefix}BgGradient`,
            type: 'gradient',
            label: 'Background gradient',
            group,
            default: defaults.bgGradient,
            visibleWhen: { field: `${prefix}BgType`, equals: 'gradient' },
        },
        {
            id: `${prefix}BorderThickness`,
            type: 'range',
            label: 'Border thickness',
            group,
            min: 0,
            max: 5,
            step: 1,
            default: 0,
            unit: 'px',
        },
        {
            id: `${prefix}BorderStyle`,
            type: 'select',
            label: 'Border style',
            group,
            default: 'solid',
            options: BORDER_STYLE_OPTIONS,
        },
        {
            id: `${prefix}BorderColor`,
            type: 'color',
            label: 'Border color',
            group,
            default: { ref: 'custom', value: defaults.borderColor },
        },
        {
            id: `${prefix}Radius`,
            type: 'range',
            label: 'Corner radius',
            group,
            min: 0,
            max: 40,
            step: 2,
            default: 8,
            unit: 'px',
        },
    ];
}
export const inventoryBlockSchema = {
    type: 'inventory',
    name: 'Inventory status',
    max: 1,
    fields: [
        visibilityField,
        // ── Inventory (when to show + threshold + quantity) ──────────────────────
        {
            id: 'showLowStockWhen',
            aiConfigurable: true,
            type: 'select',
            label: 'Show stock level',
            group: 'Inventory',
            default: 'under_threshold',
            options: [
                { value: 'under_threshold', label: 'Only under the threshold' },
                { value: 'all_the_time', label: 'All the time' },
            ],
        },
        {
            id: 'inventoryThreshold',
            type: 'range',
            label: 'Low stock threshold',
            group: 'Inventory',
            info: 'Below this quantity the "Low stock" state shows.',
            min: 0,
            max: 100,
            step: 1,
            default: 10,
            visibleWhen: { field: 'showLowStockWhen', equals: 'under_threshold' },
        },
        {
            id: 'showInventoryQuantity',
            type: 'checkbox',
            label: 'Show remaining quantity',
            group: 'Inventory',
            info: 'Append the remaining count, e.g. "Low stock: 4 left".',
            default: false,
        },
        // ── Text & styling ───────────────────────────────────────────────────────
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            group: 'Text & styling',
            min: 10,
            max: 26,
            step: 1,
            default: 16,
            unit: 'px',
        },
        {
            id: 'fontFamily',
            type: 'select',
            label: 'Font',
            group: 'Text & styling',
            default: 'body',
            options: [
                { value: 'body', label: 'Body' },
                { value: 'heading', label: 'Heading' },
            ],
        },
        {
            id: 'textAlign',
            aiConfigurable: true,
            type: 'select',
            label: 'Alignment',
            group: 'Text & styling',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        // ── Container ────────────────────────────────────────────────────────────
        {
            id: 'containerWidth',
            type: 'select',
            label: 'Width',
            group: 'Container',
            default: 'full_width',
            options: [
                { value: 'full_width', label: 'Full width' },
                { value: 'fit_content', label: 'Fit content' },
            ],
        },
        // ── Icon ─────────────────────────────────────────────────────────────────
        {
            id: 'iconSize',
            type: 'range',
            label: 'Icon size',
            group: 'Icon',
            min: 8,
            max: 28,
            step: 1,
            default: 15,
            unit: 'px',
        },
        {
            id: 'iconAnimation',
            type: 'select',
            label: 'Icon animation',
            group: 'Icon',
            default: 'none',
            options: ANIMATION_OPTIONS,
        },
        {
            id: 'iconAnimationSpeed',
            type: 'range',
            label: 'Animation speed',
            group: 'Icon',
            info: 'Duration of one animation cycle.',
            min: 100,
            max: 3000,
            step: 100,
            default: 1500,
            unit: 'ms',
            visibleWhen: { field: 'iconAnimation', notEquals: 'none' },
        },
        {
            id: 'animationScale',
            type: 'range',
            label: 'Ripple scale',
            group: 'Icon',
            info: 'How far the ripple ring expands.',
            min: 130,
            max: 250,
            step: 5,
            default: 160,
            unit: '%',
            visibleWhen: { field: 'iconAnimation', equals: 'ripple' },
        },
        {
            id: 'blinkShowOuter',
            type: 'checkbox',
            label: 'Show outer ring while blinking',
            group: 'Icon',
            default: false,
            visibleWhen: { field: 'iconAnimation', equals: 'blink' },
        },
        {
            id: 'blinkLowestOpacity',
            type: 'range',
            label: 'Blink lowest opacity',
            group: 'Icon',
            min: 0,
            max: 50,
            step: 5,
            default: 20,
            unit: '%',
            visibleWhen: { field: 'iconAnimation', equals: 'blink' },
        },
        // ── In stock ─────────────────────────────────────────────────────────────
        ...stateFields('in', 'In stock', {
            text: '#166534',
            icon: '#16a34a',
            bgColor: '#e6f7ec',
            bgGradient: 'linear-gradient(90deg,#e6f7ec,#ffffff)',
            borderColor: '#cdecdc',
        }),
        // ── Low stock ────────────────────────────────────────────────────────────
        ...stateFields('low', 'Low stock', {
            text: '#9a3412',
            icon: '#ea580c',
            bgColor: '#fff1e6',
            bgGradient: 'linear-gradient(90deg,#fff1e6,#ffffff)',
            borderColor: '#ffd7b3',
        }),
        // ── Out of stock ─────────────────────────────────────────────────────────
        ...stateFields('out', 'Out of stock', {
            text: '#991b1b',
            icon: '#dc2626',
            bgColor: '#ffe6e6',
            bgGradient: 'linear-gradient(90deg,#ffe6e6,#ffffff)',
            borderColor: '#ffc8c8',
        }),
        // ── Margins ──────────────────────────────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
/** Resolve a ColorValue|string to a literal/var, falling back to the Shrine default for the field. */
function color(v, fallback) {
    if (v && typeof v === 'object' && 'ref' in v) {
        return resolveColor(v) ?? fallback;
    }
    if (typeof v === 'string' && v)
        return v;
    return fallback;
}
/** Per-state box (background + border + radius) resolved from the `${state}…` settings. */
function stateBox(bs, state, defaults) {
    const bgType = str(bs[`${state}BgType`], 'none');
    const thickness = num(bs[`${state}BorderThickness`], 0);
    const hasBox = bgType !== 'none' || thickness > 0;
    const box = {};
    if (bgType === 'color') {
        box.background = color(bs[`${state}BgColor`], defaults.bgColor);
    }
    else if (bgType === 'gradient') {
        box.background = str(bs[`${state}BgGradient`], defaults.bgGradient);
    }
    if (thickness > 0) {
        box.border = `${thickness}px ${str(bs[`${state}BorderStyle`], 'solid')} ${color(bs[`${state}BorderColor`], defaults.borderColor)}`;
    }
    if (hasBox) {
        box.borderRadius = `${num(bs[`${state}Radius`], 8)}px`;
        box.padding = '0.4em 0.7em';
    }
    return box;
}
/**
 * Derive the active stock state + remaining quantity from the product inventory, with a demo fallback.
 * - tracked + numeric quantity → real: 0 = out, ≤ threshold = low, else in.
 * - untracked / null quantity → demo: use a low-ish sample (threshold-aware) so the preview is alive.
 */
function deriveStock(inventory, threshold) {
    if (inventory.tracked && typeof inventory.quantity === 'number') {
        const q = inventory.quantity;
        if (q <= 0)
            return { state: 'out', quantity: 0 };
        if (q <= threshold)
            return { state: 'low', quantity: q };
        return { state: 'in', quantity: q };
    }
    // Demo fallback: a believable "low stock" sample so all three colour blocks have something to show.
    const demoQty = Math.max(1, Math.min(threshold, Math.round(threshold / 2) || 4));
    return { state: 'low', quantity: demoQty };
}
const STATE_TEXT = {
    in: 'En stock',
    low: 'Stock faible',
    out: 'Rupture de stock',
};
const InventoryBlock = ({ block }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-inv-${block.id}`;
    const showWhen = str(bs.showLowStockWhen, 'under_threshold');
    const threshold = num(bs.inventoryThreshold, 10);
    const showQty = bs.showInventoryQuantity === true;
    const { state, quantity } = deriveStock(ps.product.inventory, threshold);
    // Shrine "show stock level = only under threshold": the IN-STOCK chip is hidden while plenty remain;
    // low + out always show. "All the time" shows the in-stock chip too.
    const hideInStock = showWhen === 'under_threshold' && state === 'in';
    const textSize = num(bs.textSize, 16);
    const fontFamily = str(bs.fontFamily, 'body') === 'heading'
        ? 'var(--aqx-theme-heading-font, var(--aqx-theme-font))'
        : 'var(--aqx-theme-font)';
    const justify = JUSTIFY[str(bs.textAlign, 'left')] ?? 'flex-start';
    const fullWidth = str(bs.containerWidth, 'full_width') === 'full_width';
    const iconSize = num(bs.iconSize, 15);
    const animation = str(bs.iconAnimation, 'none');
    const animSpeed = num(bs.iconAnimationSpeed, 1500);
    const rippleScale = num(bs.animationScale, 160) / 100;
    const blinkOuter = bs.blinkShowOuter === true;
    const blinkLowOpacity = num(bs.blinkLowestOpacity, 20) / 100;
    const stateDefaults = {
        in: {
            text: '#166534',
            icon: '#16a34a',
            bgColor: '#e6f7ec',
            bgGradient: 'linear-gradient(90deg,#e6f7ec,#ffffff)',
            borderColor: '#cdecdc',
        },
        low: {
            text: '#9a3412',
            icon: '#ea580c',
            bgColor: '#fff1e6',
            bgGradient: 'linear-gradient(90deg,#fff1e6,#ffffff)',
            borderColor: '#ffd7b3',
        },
        out: {
            text: '#991b1b',
            icon: '#dc2626',
            bgColor: '#ffe6e6',
            bgGradient: 'linear-gradient(90deg,#ffe6e6,#ffffff)',
            borderColor: '#ffc8c8',
        },
    };
    const def = stateDefaults[state];
    const textColor = color(bs[`${state}TextColor`], def.text);
    const iconColor = color(bs[`${state}IconColor`], def.icon);
    const box = stateBox(bs, state, def);
    // Label: base state text + optional " : {n} left" (out of stock never shows a count).
    let label = STATE_TEXT[state];
    if (showQty && state !== 'out') {
        label = `${label} : ${quantity} restant${quantity > 1 ? 's' : ''}`;
    }
    const vis = visibilityCss(cls, bs.visibility);
    // ── Icon animation CSS (pure keyframes, SSR-safe; no-JS degrades to a static dot). ──
    const dotCls = `${cls}__dot`;
    const ringCls = `${cls}__ring`;
    let animCss = '';
    if (animation === 'ripple') {
        animCss =
            `.${ringCls}{position:absolute;inset:0;border-radius:50%;background:currentColor;` +
                `animation:${ringCls}-kf ${animSpeed}ms ease-out infinite}` +
                `@keyframes ${ringCls}-kf{0%{transform:scale(1);opacity:.55}` +
                `100%{transform:scale(${rippleScale});opacity:0}}` +
                `@media(prefers-reduced-motion:reduce){.${ringCls}{animation:none;opacity:0}}`;
    }
    else if (animation === 'blink') {
        animCss =
            `.${dotCls}--blink{animation:${dotCls}-kf ${animSpeed}ms ease-in-out infinite}` +
                `@keyframes ${dotCls}-kf{0%,100%{opacity:1}50%{opacity:${blinkLowOpacity}}}` +
                `@media(prefers-reduced-motion:reduce){.${dotCls}--blink{animation:none}}`;
        if (blinkOuter) {
            animCss += `.${ringCls}{position:absolute;inset:-35%;border-radius:50%;border:1px solid currentColor;opacity:.35}`;
        }
    }
    const dotStyle = {
        position: 'relative',
        flexShrink: 0,
        inlineSize: `${iconSize}px`,
        blockSize: `${iconSize}px`,
        color: iconColor,
    };
    return (_jsxs(_Fragment, { children: [vis ? _jsx("style", { dangerouslySetInnerHTML: { __html: vis } }) : null, animCss ? _jsx("style", { dangerouslySetInnerHTML: { __html: animCss } }) : null, hideInStock ? null : (_jsx("div", { className: cls, style: {
                    display: 'flex',
                    justifyContent: justify,
                    ...marginStyle(bs),
                }, children: _jsxs("div", { style: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5em',
                        inlineSize: fullWidth ? '100%' : 'auto',
                        fontSize: `${textSize}px`,
                        fontFamily,
                        lineHeight: 1.3,
                        color: textColor,
                        ...box,
                    }, children: [_jsxs("span", { className: dotCls, style: dotStyle, "aria-hidden": "true", children: [_jsx("svg", { viewBox: "0 0 24 24", width: "100%", height: "100%", className: animation === 'blink' ? `${dotCls}--blink` : undefined, style: { display: 'block', position: 'relative', zIndex: 1 }, children: _jsx("circle", { cx: "12", cy: "12", r: "9", fill: "currentColor" }) }), animation !== 'none' ? _jsx("span", { className: ringCls, "aria-hidden": "true" }) : null] }), _jsx("span", { children: label })] }) }))] }));
};
export const inventoryBlock = {
    type: 'inventory',
    schema: inventoryBlockSchema,
    Component: InventoryBlock,
    meta: { label: 'Inventory status' },
};
//# sourceMappingURL=index.js.map